import { useCallback } from "react";
import {
  useApplyAiMappingAdditionalCosts,
  useGetAiMappingProposal,
  useSyncAiMappingCostsToMap,
} from "./hooks";
import { classifyAiMappingError } from "./aiMappingErrors";
import {
  AiMappingCostConfig,
  configToSyncItem,
  loadAiCostConfigs,
  replaceAiCostConfigs,
  toSyncKind,
} from "./aiMappingStorage";

export interface ApplyAiCostsResult {
  applied: number;
  skipped: number;
  reasons: string[];
}

const loadReconciledConfigs = (projectMapId: string): AiMappingCostConfig[] => {
  const stored = loadAiCostConfigs(projectMapId);
  const jobProductIds = Array.from(
    new Set(
      stored
        .filter((config) => config.productId && !config.additionalCostId)
        .map((config) => config.productId as string)
    )
  );
  if (jobProductIds.length !== 1) return stored;

  const [onlyProductId] = jobProductIds;
  const reconciled = Array.from(
    new Map(
      stored.map((config) => {
        const normalized = config.additionalCostId
          ? { ...config, productId: onlyProductId }
          : config;
        const key = [
          normalized.areaId,
          normalized.kind ?? "detection_count",
          normalized.category,
          normalized.productId ?? "",
          normalized.additionalCostId ?? "",
        ].join(":");
        return [key, normalized] as const;
      })
    ).values()
  );

  if (
    reconciled.length !== stored.length ||
    reconciled.some((config, index) => config.productId !== stored[index]?.productId)
  ) {
    replaceAiCostConfigs(projectMapId, reconciled);
  }
  return reconciled;
};

export default function useApplyAiCostsToProposal() {
  const [syncCostsAction] = useSyncAiMappingCostsToMap();
  const [fetchProposal] = useGetAiMappingProposal();
  const [applyCostsAction] = useApplyAiMappingAdditionalCosts();

  const ensureCostProductsOnMap = useCallback(
    async (projectMapId: string): Promise<void> => {
      const configs = loadReconciledConfigs(projectMapId).filter(
        (config) => config.productId && !config.additionalCostId
      );
      if (!configs.length) return;

      const configsByArea = new Map<string, typeof configs>();
      const assignmentKeys = new Set<string>();

      for (const config of configs) {
        if (!config.productId) continue;
        const key = [
          config.areaId,
          config.kind ?? "detection_count",
          config.category,
          config.productId,
        ].join(":");
        if (assignmentKeys.has(key)) continue;
        assignmentKeys.add(key);
        configsByArea.set(config.areaId, [...(configsByArea.get(config.areaId) ?? []), config]);
      }

      for (const [areaId, areaConfigs] of configsByArea) {
        await syncCostsAction({
          variables: {
            input: {
              areaId,
              items: areaConfigs.map(configToSyncItem),
            },
          },
        });
      }
    },
    [syncCostsAction]
  );

  const applyCostsToProposal = useCallback(
    async ({
      projectMapId,
      proposalId,
    }: {
      projectMapId: string;
      proposalId: string;
    }): Promise<ApplyAiCostsResult> => {
      const configs = loadReconciledConfigs(projectMapId).filter(
        (config) => config.additionalCostId && config.productId
      );
      if (!configs.length) return { applied: 0, skipped: 0, reasons: [] };

      const result = await fetchProposal({
        variables: { id: proposalId },
        fetchPolicy: "network-only",
      });
      const proposalProducts =
        result.data?.getProposal?.stages?.flatMap((stage) => stage.proposalProducts ?? []) ?? [];
      if (!proposalProducts.length) {
        return {
          applied: 0,
          skipped: configs.length,
          reasons: ["the proposal has no products to attach costs to"],
        };
      }

      let applied = 0;
      let skipped = 0;
      const reasons = new Set<string>();
      for (const config of configs) {
        const match = proposalProducts.find(
          (product) => product.material?.productId === config.productId
        );
        if (!match) {
          skipped += 1;
          reasons.add("the assigned product is not in the proposal");
          continue;
        }
        const result = await applyCostsAction({
          variables: {
            input: {
              proposalId,
              areaId: config.areaId,
              items: [
                {
                  category: config.category,
                  proposalProductId: match.id,
                  additionalCostId: config.additionalCostId as string,
                  kind: toSyncKind(config.kind),
                  categories: config.categories ?? null,
                },
              ],
            },
          },
        });
        if (result.success) {
          applied += 1;
        } else {
          const { message, detail } = classifyAiMappingError(result);
          skipped += 1;
          reasons.add(detail ?? message);
        }
      }
      return { applied, skipped, reasons: Array.from(reasons) };
    },
    [fetchProposal, applyCostsAction]
  );

  return { ensureCostProductsOnMap, applyCostsToProposal };
}
